import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  BaSeBadge,
  BaSeHeader,
  BaSeIcon,
  BaSeLogoSebraeInverse,
  BaSeRoot,
  BaSeShapeButton,
  BaSeTheme,
} from '@base/react'
import { colors } from '~/design'
import styled from 'styled-components'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import Modal from '~/prix/react/components/modal'
import getNotificationsDefinition from '~/packages/notifications/getNotifications/getNotifications.definition'
import readNotificationDefinition from '~/packages/notifications/readNotification/readNotification.definition'
import useAPI from '~/prix/react/hooks/api'
import useNotificationContext from '~/packages/notifications/notificationContext'
import NotificationsComponent from './notifications.component'
import { useKeycloak } from './keycloak'
import { useNavigate } from 'react-router-dom'
import { useClickAway } from 'react-use'

const NavbarLogo = styled.a`
  order: 0;
  flex-grow: 0;
  justify-self: start;
  margin-left: 0;
  cursor: pointer;
`

const NotificationButton = styled.button`
  display: flex;
  justify-content: center;
  padding: 0.75rem;
  align-items: center;
  background-color: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 255ms cubic-bezier(0.4, 0, 0.2, 1);
  margin: -12px -15px 0 0;
  transform: translateY(5px);

  &:hover,
  &:active {
    background: rgba(179, 179, 179, 0.2);
  }

  @media (max-width: ${BaSeTheme.breakpoints.mobileLarge.maxWidth}px) {
    margin: -10px 0 0;
  }
`

const NotificationAlert = styled.div`
  position: absolute;
  top: 2.5px;
  right: 8px;
  z-index: 1;
  pointer-events: none;

  > * {
    pointer-events: none;
  }
`

const NotificationMenuWrapper = styled.div`
  position: absolute;
  top: 46px;
  right: 0;
  width: 300px;
  display: grid;
  grid-template-rows: 1fr;
  background: #fff;
  border-radius: 4px;
  z-index: 1000;
  border: 1px solid ${BaSeTheme.colors.institucionais.azulSebrae90};
  transition: all 255ms cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 12px 0 rgba(20, 46, 82, 0.2);
  transform-origin: top right;
  animation: notificationMenuAppear 255ms cubic-bezier(0.4, 0, 0.2, 1);

  @keyframes notificationMenuAppear {
    from {
      grid-template-rows: 0fr;
    }

    to {
      grid-template-rows: 1fr;
    }
  }

  &::before {
    content: '';
    display: block;
    -webkit-transform: rotate(45deg) scale(1) translateY(0);
    -ms-transform: rotate(45deg) scale(1) translateY(0);
    transform: rotate(45deg) scale(1) translateY(0);
    top: -7px;
    right: calc(2em - 1px);
    height: 14px;
    width: 14px;
    position: absolute;
    background: ${BaSeTheme.colors.contrast.institucionais.azulSebrae};
    border: 1px solid transparent;
    pointer-events: none;
    border-color: ${BaSeTheme.colors.institucionais.azulSebrae90} transparent transparent
      ${BaSeTheme.colors.institucionais.azulSebrae90};
  }

  @media (max-width: ${BaSeTheme.breakpoints.mobileLarge.maxWidth}px) {
    right: auto;
    left: 20px;
    max-width: calc(100dvw - 40px);

    &::before {
      right: auto;
      left: 2.75em;
    }
  }
`

const NotificationMenuContent = styled.div`
  min-height: 0;
  overflow: hidden;
  border-radius: 4px;
`

const NotificationTitleContainer = styled.div`
  background: ${BaSeTheme.colors.contrast.institucionais.azulSebrae};
  padding: 15px;
  position: relative;
  text-align: center;
  color: ${BaSeTheme.colors.institucionais.azulSebrae};
  border-radius: 10px 10px 0 0;
  border: 0px;
  border-style: solid;
  border-bottom-width: 1px;
  border-color: ${BaSeTheme.colors.institucionais.cinzaSebrae90};
  overflow: hidden;
  font-size: 18px;
  font-weight: 700;
`

const NotificationContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: auto;
  position: relative;
  color: ${BaSeTheme.colors.institucionais.cinzaSebrae};
`

const NotificationLink = styled.a`
  width: 100%;
  text-align: center;
  font-size: 12px;
  padding: 8px;
  color: ${colors.sebraeBlue};
  cursor: pointer;
`

const NoNotificationWarning = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  gap: 12px;

  p {
    text-align: center;
    font-size: 15px;
    font-weight: 700;
    margin: 0;
  }

  span {
    text-align: center;
  }
`

const ModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px;
  height: 100%;
  overflow: hidden;
`

const NotificationModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  box-shadow: ${BaSeTheme.colors.defaultColors.shadowColor} 0px 32px 30px 0px;
  border: 2px solid ${BaSeTheme.colors.institucionais.azulSebrae90};
  border-radius: 4px;
  background-color: ${BaSeTheme.colors.contrast.institucionais.azulSebrae};
`

const NotificationListTitle = styled.div`
  display: flex;
  padding: 0 25px;
  width: 100%;
  gap: 10px;

  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  h3 {
    color: ${BaSeTheme.colors.institucionais.cinzaSebrae45};
    font-weight: 400;
    line-height: 23.3px;
    font-size: 2rem;
  }

  div {
    min-width: 3rem;
  }
`

const NotificationModalListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: ${BaSeTheme.colors.contrast.institucionais.azulSebrae};
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
    margin: 0;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${BaSeTheme.colors.institucionais.cinzaSebrae60};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${BaSeTheme.colors.institucionais.cinzaSebrae30};
  }
`

interface payloadProps {
  title: string
  body: string
}

export interface NotificationProps {
  id: string
  url: string
  label: string | null
  payload: payloadProps
  readAt: string | null
  createdAt: string | Date
}

export default function Header() {
  const dropdownRef = useRef<HTMLLIElement | null>(null)
  const navigate = useNavigate()
  const auth = useKeycloak()

  const { context } = useAPI()
  const { notifications, setNotifications, countUnreadNotifications, setCountUnreadNotifications } =
    useNotificationContext()

  const email = context.user?.email

  const [showMenu, setShowMenu] = useState(false)
  const notificationRef = useRef<any | null>(null)
  const [showNotification, setShowNotification] = useState<boolean>(false)
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState<boolean>(false)

  const { callAction: callActionGetNotifications } = useActionFunction(getNotificationsDefinition)
  const { callAction: callActionMarkAsRead } = useActionFunction(readNotificationDefinition)

  useEffect(() => {
    if (!email || !auth.isAuthenticated) {
      return
    }

    const { resultPromise } = callActionGetNotifications({
      userEmail: email,
    })

    resultPromise.then((res: any) => {
      setNotifications(res)
    })
  }, [auth.isAuthenticated, email, showNotification])

  useEffect(() => {
    const countUnreadNotifications = notifications.reduce((count, notification) => {
      return notification.readAt === null ? count + 1 : count
    }, 0)

    setCountUnreadNotifications(countUnreadNotifications)
  }, [notifications])

  useClickAway(dropdownRef, () => setShowMenu(false), ['click'])
  useClickAway(notificationRef, () => setShowNotification(false), ['click'])

  const markAllAsRead = () => {
    if (countUnreadNotifications > 0 && email) {
      callActionMarkAsRead({
        userEmail: email,
      })

      setCountUnreadNotifications(0)
      const updatedNotifications = notifications.map(notification => ({
        ...notification,
        readAt: String(new Date()),
      }))

      setNotifications(updatedNotifications)
    }
  }

  const handleClickLogo = useCallback(() => {
    navigate('/app')
  }, [navigate])

  const notificationButton = useMemo(
    () => (
      <>
        {auth.isAuthenticated && (
          <>
            <NotificationAlert>
              <BaSeBadge
                value={countUnreadNotifications}
                valueNumberLimit={99}
                borderRadius={16}
                color='destructive'
                show={true}
                height={24}
              />
            </NotificationAlert>
            <NotificationButton
              ref={notificationRef}
              onClick={() => setShowNotification(!showNotification)}
            >
              <BaSeIcon
                name='media/notification-2-line'
                color='#fff'
                size={24}
                description='Notificações'
              />
            </NotificationButton>

            {showNotification && (
              <React.Fragment>
                <NotificationMenuWrapper
                  onClick={(e: { stopPropagation: () => void }) => e.stopPropagation()}
                >
                  <NotificationMenuContent>
                    <NotificationTitleContainer>Notificações</NotificationTitleContainer>
                    <NotificationContentWrapper>
                      {notifications && notifications.length > 0 ? (
                        <>
                          {notifications
                            .slice(0, 3)
                            .map((notification: NotificationProps, index: number) => (
                              <NotificationsComponent
                                isModal={false}
                                notification={notification}
                                setRead={markAllAsRead}
                                key={index}
                              />
                            ))}
                          <NotificationLink
                            onClick={() => {
                              setShowNotification(false)
                              setIsNotificationModalOpen(true)
                            }}
                          >
                            Ver mais
                          </NotificationLink>
                        </>
                      ) : (
                        <NoNotificationWarning>
                          Não há nenhuma notificação no momento.
                        </NoNotificationWarning>
                      )}
                    </NotificationContentWrapper>
                  </NotificationMenuContent>
                </NotificationMenuWrapper>
              </React.Fragment>
            )}
          </>
        )}
      </>
    ),
    [auth.isAuthenticated, countUnreadNotifications, showNotification, notifications],
  )

  return (
    <BaSeRoot locale='pt-BR' withResetCss={false}>
      <BaSeHeader
        menuItens={[
          {
            onClick: () => {
              window.open('https://prix.tech/#Contact', '_blank')
            },
            label: 'Fale conosco',
          },
          {
            component: (closeMenu: () => void) => {
              return notificationButton
            },
            hideOnSidebar: true,
          },
        ]}
        customComponents={[
          {
            component: (closeMenu: () => void) => {
              return notificationButton
            },
            showOnCollapsed: true,
            showOnExpanded: false,
            showOnSidebar: false,
            showOnSubmenu: false,
          },
        ]}
        textColor={BaSeTheme.colors.contrast.institucionais.azulSebrae36}
        backgroundColor={BaSeTheme.colors.institucionais.azulSebrae36}
        sidebarBackgroundColor={BaSeTheme.colors.institucionais.azulSebrae20}
        sidebarTextColor={BaSeTheme.colors.contrast.institucionais.azulSebrae20}
        systemName='Monitor'
        accountProfileProps={{
          authenticated: !!auth.isAuthenticated,
          userEmail: context.user?.email,
          clientName: context.user?.name,
          keycloak: auth.keycloak,
          accountMenuItems: {
            changePassword: {
              show: false,
            },
            myBusiness: {
              show: false,
            },
            linkSocialNetworks: {
              show: false,
            },
          },
        }}
        collapsedBreakpoint='onMobileLarge'
        systemLogo={() => (
          <NavbarLogo onClick={handleClickLogo}>
            <BaSeLogoSebraeInverse width={75} />
          </NavbarLogo>
        )}
      />

      <Modal
        isOpen={isNotificationModalOpen}
        onClose={() => {
          setIsNotificationModalOpen(false)
          markAllAsRead()
        }}
        hideCloseButton
        customStyles={{
          content: {
            background: 'transparent',
          },
        }}
      >
        <ModalWrapper>
          <NotificationModalContainer>
            <NotificationListTitle>
              <h3>Notificações</h3>
              <BaSeShapeButton
                buttonType='button'
                color='default'
                nameIcon='close'
                onClick={() => {
                  setIsNotificationModalOpen(false)
                  markAllAsRead()
                }}
                shape='circle'
                descriptionIcon='Fechar'
                sizeIcon='big'
                type='tertiary'
              />
            </NotificationListTitle>
            <NotificationModalListWrapper>
              {notifications &&
                notifications.map((notification: NotificationProps, index: number) => (
                  <NotificationsComponent
                    isModal={true}
                    notification={notification}
                    setRead={markAllAsRead}
                    key={index}
                  />
                ))}
            </NotificationModalListWrapper>
          </NotificationModalContainer>
        </ModalWrapper>
      </Modal>
    </BaSeRoot>
  )
}
